import {
	startOfDay,
	endOfDay,
	subDays,
	startOfWeek,
	endOfWeek,
	startOfMonth,
	endOfMonth,
	format,
	subHours,
	differenceInDays,
	isSameDay,
	differenceInHours,
	getYear,
	endOfToday,
	startOfToday,
	startOfYear,
	subYears,
} from "date-fns";

interface DateRange {
	start: Date | string;
	end: Date | string;
}

export function isLessThan24Hours({ start, end }: { start: string; end: string }): boolean {
	const startTime = new Date(start);
	const endTime = new Date(end);

	const timeDifference = endTime.getTime() - startTime.getTime();
	const twentyFourHoursInMillis = 24 * 60 * 60 * 1000;

	return timeDifference < twentyFourHoursInMillis;
}

export function getTodayPeriod(): DateRange {
	const today = new Date();
	return { start: startOfDay(today), end: getEndAt() };
}

export function getYesterdayPeriod(): DateRange {
	const yesterday = subDays(new Date(), 1);
	return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
}

export function getLast15DaysPeriod(): DateRange {
	const last15DaysStart = subDays(new Date(), 15);
	const today = new Date();
	return { start: startOfDay(last15DaysStart), end: endOfDay(today) };
}

export function getLastWeekPeriod(): DateRange {
	const lastWeekStart = startOfWeek(subDays(new Date(), 7));
	const lastWeekEnd = endOfWeek(subDays(new Date(), 7));
	return { start: startOfDay(lastWeekStart), end: endOfDay(lastWeekEnd) };
}

export function getLastMonthPeriod(): DateRange {
	const lastMonthStart = startOfMonth(subDays(new Date(), 30));
	const lastMonthEnd = endOfMonth(subDays(new Date(), 30));
	return { start: startOfDay(lastMonthStart), end: endOfDay(lastMonthEnd) };
}

export function getEndAt(): string {
	return format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
}

export function getStartAt(daysAgo: number): string {
	return format(subDays(new Date(), daysAgo), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
}

export const INIT_TODAY = startOfToday();
export const END_TODAY = endOfToday();

export function getLastYearToTodayPeriod(): DateRange {
	return {
		start: startOfDay(subYears(INIT_TODAY, 1)),
		end: END_TODAY,
	};
}

export function formatDateObjectToString(dateObject: any): string {
	const formattedStartDate = format(dateObject.start, "EEE dd/MM/yy HH:mm");
	const formattedEndDate = format(dateObject.end, "EEE dd/MM/yy HH:mm");

	return `${formattedStartDate} - ${formattedEndDate}`;
}

export const isJanuaryFirst = (date: any) => {
	return date.month() === 0 && date.date() === 1;
};

export function getLast24HoursPeriod(): DateRange {
	const currentDate = new Date();
	const twentyFourHoursAgo = subHours(currentDate, 24);

	const formattedStartDate = format(twentyFourHoursAgo, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
	const formattedEndDate = format(currentDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

	return { start: formattedStartDate, end: formattedEndDate };
}

// Converte uma data string para formato UTC (ISO 8601)
export function toUTC(dateString: string): string {
	return new Date(dateString).toISOString();
}

export function remove3HoursFromDate(date: string): Date {
	const dateObj = new Date(date);
	return subHours(dateObj, 3);
}

export function formatDateToCustomFormat(date: Date | string): string {
	const dateObj = new Date(date);
	const adjustedDate = subHours(dateObj, 3);
	return format(adjustedDate, "dd/MM/yyyy HH:mm");
}

export function getDifferenceInDaysOrHours(startDate: Date | string, endDate: Date | string): string {
	const start = new Date(startDate);
	const end = new Date(endDate);

	if (isSameDay(start, end)) {
		const hoursDifference = differenceInHours(end, start);
		return `${hoursDifference} horas`;
	} else {
		const daysDifference = differenceInDays(end, start);
		return `${daysDifference} dias`;
	}
}

export function convertFormattedDateToUTCMinus3(dateString: string): string {
	if (!dateString) return "-";

	try {
		// Parse date format: "dd/MM/yyyy - HH:mm"
		const match = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(\d{2}):(\d{2})$/);

		if (!match) {
			// If format doesn't match, return as is
			return dateString;
		}

		const [, day, month, year, hours, minutes] = match;

		// Create date object (month is 0-indexed in JavaScript)
		const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));

		// Subtract 3 hours for UTC-3
		const adjustedDate = subHours(date, 3);

		// Format back to "dd/MM/yyyy - HH:mm"
		return format(adjustedDate, "dd/MM/yyyy - HH:mm");
	} catch (error) {
		return dateString;
	}
}

export function getDateFormatByLocale(locale: string): string {
	const lang = locale.split("-")[0];
	if (lang === "en") return "MM/dd/yyyy";
	return "dd/MM/yyyy";
}

export function getDateTimeFormatByLocale(locale: string): string {
	const lang = locale.split("-")[0];
	if (lang === "en") return "MM/dd/yyyy - HH:mm";
	return "dd/MM/yyyy - HH:mm";
}

export function parseDateStringToDate(dateString: string): Date | null {
	if (!dateString || typeof dateString !== "string") return null;
	const trimmed = dateString.trim();
	const isoMatch = /^\d{4}-\d{2}-\d{2}/.test(trimmed);
	if (isoMatch) {
		const parsed = new Date(trimmed.includes(" ") ? trimmed.replace(" ", "T") : trimmed);
		return isNaN(parsed.getTime()) ? null : parsed;
	}
	const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(\d{2}):(\d{2})(?::(\d{2}))?/);
	if (match) {
		const [, day, month, year, hours, minutes, seconds] = match;
		const parsed = new Date(
			parseInt(year, 10),
			parseInt(month, 10) - 1,
			parseInt(day, 10),
			parseInt(hours, 10),
			parseInt(minutes, 10),
			seconds ? parseInt(seconds, 10) : 0,
		);
		return isNaN(parsed.getTime()) ? null : parsed;
	}
	const matchNoDash = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
	if (matchNoDash) {
		const [, day, month, year, hours, minutes] = matchNoDash;
		const parsed = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), parseInt(hours, 10), parseInt(minutes, 10));
		return isNaN(parsed.getTime()) ? null : parsed;
	}
	const parsed = new Date(trimmed);
	return isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDateByLocale(date: Date | string, locale: string, pattern?: string): string {
	let dateObj: Date;
	if (typeof date === "string") {
		dateObj = parseDateStringToDate(date) ?? new Date(date);
	} else {
		dateObj = date;
	}
	if (isNaN(dateObj.getTime())) return "-";
	const adjustedDate = subHours(dateObj, 3);
	const formatPattern = pattern ?? getDateTimeFormatByLocale(locale);
	return format(adjustedDate, formatPattern);
}
